import { useEffect, useRef, useState } from 'react'
import { HStack, Input, NativeSelect } from '@chakra-ui/react'
import type { CustomFieldProps } from '@/shared/ui/form-builder'

/**
 * A 24-hour replacement for `<input type="datetime-local">`.
 *
 * The native control takes its 12/24-hour format from the browser's UI
 * language, which turns it into an AM/PM picker on an English profile and
 * cannot be overridden from the page. The date half is still native — only
 * the clock is rebuilt, as an hour and a minute select.
 *
 * The value keeps the native `YYYY-MM-DDTHH:mm` shape, so nothing downstream
 * (validation, the payload mapper) has to know the difference.
 */

interface Parts {
  date: string
  hour: string
  minute: string
}

const EMPTY_PARTS: Parts = { date: '', hour: '', minute: '' }

const pad = (value: number) => String(value).padStart(2, '0')

const HOURS = Array.from({ length: 24 }, (_, hour) => pad(hour))
const MINUTES = Array.from({ length: 60 }, (_, minute) => pad(minute))

const splitValue = (value: string): Parts => {
  if (!value) return EMPTY_PARTS

  const [date = '', time = ''] = value.split('T')
  const [hour = '', minute = ''] = time.split(':')
  return { date, hour, minute }
}

/** Only a whole moment is a value — a half-picked one reads as empty. */
const joinParts = ({ date, hour, minute }: Parts) =>
  date && hour && minute ? `${date}T${hour}:${minute}` : ''

export function DateTime24Input({
  value,
  onChange,
  invalid,
}: CustomFieldProps) {
  // The three parts are filled one at a time, so they live here rather than
  // being re-derived from the form value: an incomplete combination has no
  // form value to derive from, and echoing an empty one back would clear the
  // day the user had just typed.
  const [parts, setParts] = useState<Parts>(() => splitValue(value))
  const emitted = useRef(value)

  // Adopt values set from the outside (prefill, reset) — but not the ones this
  // component itself just sent up.
  useEffect(() => {
    if (value === emitted.current) return
    emitted.current = value
    setParts(splitValue(value))
  }, [value])

  const update = (patch: Partial<Parts>) => {
    const next = { ...parts, ...patch }
    setParts(next)

    const nextValue = joinParts(next)
    if (nextValue === emitted.current) return

    emitted.current = nextValue
    onChange(nextValue)
  }

  return (
    <HStack gap={2} w="full">
      <Input
        type="date"
        value={parts.date}
        onChange={(event) => update({ date: event.target.value })}
        size="sm"
        bg="bg.surface"
        borderColor={invalid ? undefined : 'border'}
        borderRadius="md"
        flex="1"
      />
      <NativeSelect.Root size="sm" w="auto" invalid={invalid}>
        <NativeSelect.Field
          value={parts.hour}
          onChange={(event) => update({ hour: event.target.value })}
          bg="bg.surface"
          borderRadius="md"
        >
          <option value="">--</option>
          {HOURS.map((hour) => (
            <option key={hour} value={hour}>
              {hour}
            </option>
          ))}
        </NativeSelect.Field>
        <NativeSelect.Indicator />
      </NativeSelect.Root>
      <NativeSelect.Root size="sm" w="auto" invalid={invalid}>
        <NativeSelect.Field
          value={parts.minute}
          onChange={(event) => update({ minute: event.target.value })}
          bg="bg.surface"
          borderRadius="md"
        >
          <option value="">--</option>
          {MINUTES.map((minute) => (
            <option key={minute} value={minute}>
              {minute}
            </option>
          ))}
        </NativeSelect.Field>
        <NativeSelect.Indicator />
      </NativeSelect.Root>
    </HStack>
  )
}
